import React, { useState } from 'react';
import {
	LoadingOverlay,
	TextInput,
	Title,
	Button,
	Text,
	Notification,
	useMantineTheme,
	Box,
} from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import { HeroImg } from '@/components/partials/heroImg/HeroImg';
import { AppPaths } from '@/configs/appPaths';
import { SubmitStatus } from '../login/Login';
import { useContent } from '@/providers/ContentProvider';
import { isAppError } from '@/utils/utilities';
import { ErrorCode } from '@/types/types';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import styles from './ResetPassword.module.scss';

const initialValues = {
	email: '',
};

const baseSlug = 'resetPassword.';

export const ResetPassword: React.FC = () => {
	const { other } = useMantineTheme();
	const [status, setStatus] = useState(SubmitStatus.THROTTLE);
	const [resultMessage, setResultMessage] = useState('');

	const { getErrorMessage, getContent } = useContent();

	const form = useForm({
		initialValues,
		validate: {
			email: isEmail(getErrorMessage([ErrorCode.INVALID_EMAIL])),
		},
	});

	const handleSubmit = form.onSubmit(async (values) => {
		try {
			setStatus(SubmitStatus.LOADING);
			const response = await UnauthenticatedApi.requestResetPassword(
				values.email
			);

			if (response === SubmitStatus.SUCCESS) {
				form.reset();
				setStatus(SubmitStatus.SUCCESS);
				setResultMessage(getContent(`${baseSlug}success`));
				return;
			}

			setStatus(SubmitStatus.ERROR);
			form.setFieldError('email', getErrorMessage([ErrorCode.USER_NOT_FOUND]));
		} catch (error) {
			setStatus(SubmitStatus.ERROR);

			if (isAppError(error)) {
				setResultMessage(getErrorMessage(error.stack));
			} else {
				console.error('Reset password error:', error);
				setResultMessage(getErrorMessage([ErrorCode.GENERIC]));
			}
		}
	});

	return (
		<div className="page-wrapper-unauth">
			<form className={styles.form} onSubmit={handleSubmit}>
				<LoadingOverlay visible={status === SubmitStatus.LOADING} />
				<Title ta="center" order={1} mb="md">
					{getContent(`${baseSlug}header`)}
				</Title>
				<Text size="lg" ta="center" c="dimmed" mb="xl">
					{getContent(`${baseSlug}subheader`)}
				</Text>

				{status === SubmitStatus.SUCCESS && (
					<Notification
						data-testid="success-notification"
						{...other.notificationProps.success}
						mb="lg"
					>
						<Text size="md">{resultMessage}</Text>
					</Notification>
				)}

				{status !== SubmitStatus.SUCCESS && (
					<>
						<TextInput
							data-testid="email-input"
							{...form.getInputProps('email')}
							required
							type="email"
							placeholder={getContent(`${baseSlug}inputEmailPlaceholder`)}
							mb="lg"
						/>
						<Button data-testid="submit-button" w="100%" type="submit" mb="lg">
							{getContent(`${baseSlug}submitBtn`)}
						</Button>
					</>
				)}
				<Box ta="center" c="dimmed" size="md" mb="lg">
					{getContent(`${baseSlug}navigateLogin`, {
						links: {
							$LOGIN: {
								to: `/${AppPaths.LOGIN}`,
								className: 'injected-anchor ',
							},
						},
					})}
				</Box>
			</form>
			<HeroImg src={getContent(`${baseSlug}heroImg`)} />
		</div>
	);
};
